"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

type TitleResource = {
	id: number;
	name: string;
	is_owner: boolean;
};

interface StudyResourceTitleProps {
	resource_slug?: string;
}

export const StudyResourceTitle = ({ resource_slug }: StudyResourceTitleProps) => {
	const query_client = useQueryClient();
	const [is_editing, set__is_editing] = useState(false);
	const [is_saving, set__is_saving] = useState(false);
	const [title, set__title] = useState("Untitled");
	const [draft_title, set__draft_title] = useState("Untitled");
	const saving_ref = useRef(false);
	const input_ref = useRef<HTMLInputElement>(null);

	const { data: resource, isLoading } = useQuery<TitleResource>({
		queryKey: ["resource-title", resource_slug],
		enabled: Boolean(resource_slug),
		refetchOnWindowFocus: false,
		queryFn: async () => {
			if (!resource_slug) throw new Error("Missing resource slug");

			const response = await fetch(`/api/resources/${resource_slug}?fields=id,name`, {
				cache: "no-store",
			});

			if (!response.ok) throw new Error("Failed to fetch resource");

			return await response.json();
		},
	});

	useEffect(() => {
		if (!resource?.name) return;
		set__title(resource.name);
	}, [resource?.name]);

	useEffect(() => {
		if (is_editing) return;
		set__draft_title(title);
	}, [is_editing, title]);

	useEffect(() => {
		if (!is_editing) return;
		input_ref.current?.focus();
		input_ref.current?.select();
	}, [is_editing]);

	const can_edit_title = Boolean(resource?.id) && resource?.is_owner === true;

	const commit_title = async () => {
		if (saving_ref.current || is_saving) return;

		const trimmed_title = draft_title.trim();
		if (!trimmed_title || !resource?.id) {
			set__draft_title(title);
			set__is_editing(false);
			return;
		}

		if (trimmed_title === title) {
			set__is_editing(false);
			return;
		}

		saving_ref.current = true;
		set__is_saving(true);
		try {
			const response = await fetch("/api/resources/rename", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					file_id: resource.id,
					name: trimmed_title,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error?.message || "Failed to rename resource");
			}

			set__title(trimmed_title);
			set__draft_title(trimmed_title);
			query_client.setQueryData<TitleResource | undefined>(["resource-title", resource_slug], (cached) => {
				if (!cached) return cached;
				return {
					...cached,
					name: trimmed_title,
				};
			});
			query_client.setQueryData<{ id?: number; name?: string } | undefined>(["resource", resource_slug], (cached) => {
				if (!cached) return cached;
				return {
					...cached,
					name: trimmed_title,
				};
			});
			await Promise.all([
				query_client.invalidateQueries({ queryKey: ["resource-title", resource_slug] }),
				query_client.invalidateQueries({ queryKey: ["resource", resource_slug] }),
				query_client.invalidateQueries({ queryKey: ["files"] }),
			]);
		} catch (error) {
			console.error("Failed to rename resource from navbar:", error);
			toast.error("Could not update title");
			set__draft_title(title);
		} finally {
			saving_ref.current = false;
			set__is_saving(false);
			set__is_editing(false);
		}
	};

	const on_input_key_down = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			void commit_title();
			return;
		}

		if (event.key === "Escape") {
			event.preventDefault();
			set__draft_title(title);
			set__is_editing(false);
		}
	};

	if (isLoading) {
		return (
			<div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
				<Loader2 className="h-4 w-4 animate-spin" />
				<span className="truncate">Loading resource...</span>
			</div>
		);
	}

	if (is_editing) {
		return (
			<input
				ref={input_ref}
				value={draft_title}
				onChange={(event) => set__draft_title(event.target.value)}
				onBlur={() => {
					void commit_title();
				}}
				onKeyDown={on_input_key_down}
				maxLength={80}
				disabled={is_saving}
				aria-label="Edit study resource title"
				className="w-[min(52vw,560px)] min-w-0 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-hidden ring-offset-0 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 sm:text-base"
			/>
		);
	}

	if (!can_edit_title) {
		return (
			<span className="truncate px-1 py-1 text-sm font-semibold text-slate-800 sm:text-base">
				{title}
			</span>
		);
	}

	return (
		<button
			type="button"
			onClick={() => set__is_editing(true)}
			disabled={is_saving}
			className="group flex min-w-0 max-w-[min(52vw,560px)] items-center gap-2 rounded-md px-1 py-1 text-left transition-colors hover:bg-slate-100"
			aria-label={`Edit title ${title}`}
		>
			<span className="truncate text-sm font-semibold text-slate-800 sm:text-base">{title}</span>
			<Pencil className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-colors group-hover:text-slate-600" />
		</button>
	);
};
